<!--
  IGNTR sign-in page.
  Split layout: form on the left, dark hero panel on the right.

  All functional wiring (loginForm, submit, Office 365, error/loading state,
  Transloco labels) is identical to the previous implementation — only the
  presentation changed. New visual elements (top links, stat cards, footer,
  testimonial copy) are design-only and not yet wired to backend data.
-->
<div
  class="igntr-theme igntr-canvas flex min-h-screen w-full overflow-hidden bg-canvas font-sans text-slate-900"
>
  <!-- ============================== LEFT (form) ============================ -->
  <section
    class="relative flex w-full flex-col px-6 py-7 lg:w-1/2 lg:px-12 lg:py-8"
  >
    <!-- Top utility nav (design only) — right-aligned, with thin pipe
         separators matching the mockup. Uses arbitrary `gap-[4px]` instead
         of `gap-1` to avoid Bootstrap's higher-precedence `.gap-1` utility
         rule. -->
    <nav class="flex items-center justify-end gap-[4px] text-[11.5px] text-slate-500">
      <button
        type="button"
        (click)="toggleLanguage()"
        class="inline-flex items-center gap-1.5 px-2 py-1 text-slate-500 hover:text-slate-800"
      >
        <igntr-icon name="globe" [size]="13"></igntr-icon>
        {{ currentLang }}
      </button>
      <!-- Hidden for now — Help and Status not wired up
      <span aria-hidden="true" class="text-slate-300">|</span>
      <a class="px-2 py-1 text-slate-500 hover:text-slate-800" href="#">{{ "help" | transloco }}</a>
      <span aria-hidden="true" class="text-slate-300">|</span>
      <a class="px-2 py-1 text-slate-500 hover:text-slate-800" href="#">{{ "status" | transloco }}</a>
      -->
    </nav>

    <!-- Form -->
    <div class="mx-auto flex w-full max-w-[420px] flex-1 flex-col justify-center pt-12 pb-8">
      <div class="mb-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand">
        <span class="h-1 w-1 rounded-full bg-brand"></span>
        {{ "welcomeAgain" | transloco }}
      </div>

      <h1 class="mb-3 font-serif text-[36px] font-semibold leading-[1.05] tracking-[-0.025em] text-slate-900">
        {{ "signinTagline" | transloco }}
      </h1>

      <p class="mb-7 text-[13.5px] leading-relaxed text-slate-500">
        {{ "signinTaglineSubtitle" | transloco }}
      </p>

      <!-- Email + password form (unchanged wiring) -->
      <ng-container *ngIf="!enable365">
        <form
          [formGroup]="loginForm"
          (ngSubmit)="submit()"
          class="flex flex-col gap-4"
        >
          <!-- Email -->
          <div class="flex w-full flex-col gap-1.5">
            <label
              for="email"
              class="block text-[12.5px] font-medium text-slate-700"
            >
              {{ "email" | transloco }}
            </label>
            <div
              class="relative w-full"
              [class.is-invalid]="
                loginForm.get('email')?.errors && loginForm.get('email')?.touched
              "
            >
              <span class="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                <igntr-icon name="mail" [size]="16"></igntr-icon>
              </span>
              <input
                formControlName="email"
                type="email"
                id="email"
                placeholder="admin@example.com"
                class="block bg-white py-2.5 pl-10 pr-3 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
                style="width: 100%; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 12px;"
              />
            </div>
          </div>

          <!-- Password -->
          <div class="flex w-full flex-col gap-1.5">
            <label
              for="password"
              class="block text-[12.5px] font-medium text-slate-700"
            >
              {{ "password" | transloco }}
            </label>
            <div
              class="relative w-full"
              [class.is-invalid]="
                loginForm.get('password')?.errors &&
                loginForm.get('password')?.touched
              "
            >
              <span class="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                <igntr-icon name="lock" [size]="16"></igntr-icon>
              </span>
              <input
                formControlName="password"
                [type]="passwordVisible ? 'text' : 'password'"
                id="password"
                placeholder="••••••••••"
                class="block bg-white py-2.5 pl-10 pr-10 text-[13.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
                style="width: 100%; box-sizing: border-box; border: 1px solid #cbd5e1; border-radius: 12px;"
              />

              <!-- Eye icon: toggles the input type between password and text. -->
              <button
                type="button"
                tabindex="-1"
                (click)="passwordVisible = !passwordVisible"
                class="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600"
                [attr.aria-label]="passwordVisible ? 'Hide password' : 'Show password'"
                [attr.aria-pressed]="passwordVisible"
              >
                <igntr-icon name="eye" [size]="16"></igntr-icon>
              </button>

              <button
                type="button"
                class="absolute -right-9 top-1/2 -translate-y-1/2 rounded-full p-1 text-rose-500"
                data-bs-toggle="tooltip"
                data-bs-placement="left"
                id="passworderror"
                [hidden]="!title"
                [title]="title"
              >
                <i class="bi bi-info-circle"></i>
              </button>
            </div>
          </div>

          <!-- Remember me + forgot password.
               `flex-wrap gap-x-4 gap-y-2` so the two items keep their own
               line-box on a tight mobile viewport (they were collapsing
               into one run of text on narrow screens). -->
          <div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 pt-1 text-[13px]">
            <label class="flex cursor-pointer items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                formControlName="rememberMe"
                id="rememberMe"
                class="form-checkbox h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
              />
              <span>{{ "rememberMe" | transloco }}</span>
            </label>
            <a
              routerLink="/forgotpassword"
              class="font-medium text-brand-hot no-underline hover:underline"
            >
              {{ "forgotYourPassword" | transloco }}
            </a>
          </div>

          <!-- Submit -->
          <button
            type="submit"
            [disabled]="repo.isLoading$ | async"
            class="mt-2 flex w-full items-center justify-center gap-2 rounded-[10px] bg-brand px-4 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition hover:bg-brand-hot focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:opacity-60"
          >
            <ng-container *ngIf="!(repo.isLoading$ | async)">
              {{ "signIn" | transloco }}
              <igntr-icon name="arrowR" [size]="16"></igntr-icon>
            </ng-container>
            <app-spinner *ngIf="repo.isLoading$ | async"></app-spinner>
          </button>

          <!-- Trust badge (design only) -->
          <p class="mt-4 inline-flex items-center justify-center gap-1.5 text-center font-mono text-[10.5px] uppercase tracking-[0.14em] text-slate-400">
            <igntr-icon name="shield" [size]="12"></igntr-icon>
            {{ "protectedByIgntr" | transloco }}
          </p>
        </form>
      </ng-container>

      <!-- Office 365 alternative -->
      <button
        *ngIf="enable365"
        type="button"
        (click)="loadSettings()"
        class="inline-flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[14.5px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
      >
        <img
          src="/assets/img/Office365Logo.png"
          width="20"
          height="24"
          onerror="this.style.display = 'none'"
          alt=""
        />
        {{ "loginUsingOffice365" | transloco }}
      </button>

      <app-error-alert
        *ngIf="error"
        class="mt-4"
        title="{{ 'alreadyLoginedByAnotherMicrosoftAccount' | transloco }}"
        errors="error"
      ></app-error-alert>
    </div>

    <!-- Footer -->
    <footer class="flex items-center justify-between text-[11.5px] text-slate-400">
      <div>© 2026 IGNTR</div>
      <!-- Hidden for now — Privacy / Terms / DPA pages not wired up
      <div class="hidden gap-4 sm:flex">
        <a class="text-slate-400 hover:text-slate-600" href="#">{{ "privacy" | transloco }}</a>
        <a class="text-slate-400 hover:text-slate-600" href="#">{{ "terms" | transloco }}</a>
        <a class="text-slate-400 hover:text-slate-600" href="#">DPA</a>
      </div>
      -->
    </footer>
  </section>

  <!-- ============================== RIGHT (hero) =========================== -->
  <aside
    class="relative hidden flex-col overflow-hidden bg-sidebar-bg px-8 py-7 text-white lg:flex lg:w-1/2 lg:px-12 lg:py-8"
  >
    <!-- Ambient glow — the "flame" in the mockup is actually two overlapping
         radial gradients, not an SVG. Bottom-right gets the warm orange glow;
         top-left gets a subtle purple/pink wash. -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 opacity-90"
      style="
        background:
          radial-gradient(60% 50% at 75% 65%, oklch(0.55 0.2 30 / 0.55), transparent 70%),
          radial-gradient(45% 40% at 35% 25%, oklch(0.5 0.18 350 / 0.25), transparent 70%);
      "
    ></div>

    <!-- Subtle grid overlay (matches the mockup's faint background grid) -->
    <svg
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 h-full w-full opacity-[0.04]"
      preserveAspectRatio="none"
    >
      <defs>
        <pattern id="igntr-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M40 0H0V40" fill="none" stroke="white" stroke-width="0.5"></path>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#igntr-grid)"></rect>
    </svg>

    <!-- Flame watermark — exact SVG ported from the mockup. The path uses a
         100x100 viewBox (different from our 24x24 icon flame). Anchored
         bottom-right with a subtle drift animation. -->
    <div
      aria-hidden="true"
      class="pointer-events-none absolute right-[-40px] bottom-[-40px] h-[480px] w-[480px] igntr-flame-drift"
    >
      <svg viewBox="0 0 100 100" class="h-full w-full opacity-20">
        <path
          d="M50 5 C57 25 78 35 78 60 C78 78 65 90 50 90 C35 90 22 78 22 60 C22 50 28 42 35 38 C34 48 40 54 47 54 C55 54 58 48 58 42 C58 30 50 22 50 5 Z"
          fill="oklch(0.67 0.17 47)"
        ></path>
      </svg>
    </div>

    <!-- Top bar -->
    <div class="relative flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <igntr-flame [size]="26"></igntr-flame>
        <span class="text-[17px] font-semibold tracking-[0.20em]">IGNTR</span>
      </div>
      <div class="inline-flex items-center gap-2 font-mono text-[11px] text-white/55">
        <span class="h-1.5 w-1.5 rounded-full bg-emerald-400"></span>
        {{ "statusOperational" | transloco }}
      </div>
    </div>

    <!-- Stat cards (design only). Border color is set via inline style so
         it bypasses my `.igntr-theme *` reset that sets border-color to
         slate-200. The color is a slightly lifted variant of the sidebar bg
         (oklch 0.17 → 0.28) so the border reads as gray, not white. -->
    <div class="relative mt-auto flex justify-start gap-2.5">
      <div
        class="w-[152px] rounded-xl bg-white/[0.02] px-3.5 py-3 backdrop-blur"
        style="border: 1px solid oklch(0.28 0.02 265);"
      >
        <div class="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
          {{ "statSprints" | transloco }}
        </div>
        <div class="mt-2 text-[22px] font-semibold leading-none">128</div>
        <div class="mt-2 font-mono text-[10.5px] text-emerald-400">{{ "sprintsIncrease" | transloco }}</div>
      </div>
      <div
        class="w-[152px] rounded-xl bg-white/[0.02] px-3.5 py-3 backdrop-blur"
        style="border: 1px solid oklch(0.28 0.02 265);"
      >
        <div class="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
          {{ "statUsers" | transloco }}
        </div>
        <div class="mt-2 text-[22px] font-semibold leading-none">2.4K</div>
        <div class="mt-2 font-mono text-[10.5px] text-white/45">{{ "onNTenants" | transloco }}</div>
      </div>
      <div
        class="w-[152px] rounded-xl bg-white/[0.02] px-3.5 py-3 backdrop-blur"
        style="border: 1px solid oklch(0.28 0.02 265);"
      >
        <div class="font-mono text-[10px] uppercase tracking-[0.14em] text-white/45">
          {{ "statStreak" | transloco }}
        </div>
        <div class="mt-2 text-[22px] font-semibold leading-none">9 <span class="text-[12px] font-normal text-white/55">{{ "streakDays" | transloco }}</span></div>
        <div class="mt-2 inline-flex items-center gap-1 font-mono text-[10.5px] text-brand">
          <igntr-icon name="fire" [size]="11"></igntr-icon>
          {{ "streakRecord" | transloco }}
        </div>
      </div>
    </div>

    <!-- Testimonial — exact port from the mockup. The quote mark is a
         PLAIN ASCII `"` (U+0022), NOT `&ldquo;` (U+201C). In Fraunces, the
         ASCII straight quote renders as two separated apostrophe shapes
         while the curly quote renders as a single connected glyph. Size,
         weight and layout match the template inspection. -->
    <div class="relative mt-6 max-w-[560px]">
      <div class="mb-1 font-serif text-[64px] font-medium leading-none text-brand">"</div>
      <blockquote class="-mt-8 ml-6 font-serif text-[28px] font-normal leading-[1.18] text-white/95">
        {{ "testimonialQuote" | transloco }}
      </blockquote>
      <footer class="ml-6 mt-5 flex items-center gap-3">
        <div
          class="flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-semibold"
          style="background: linear-gradient(135deg, oklch(0.72 0.12 30), oklch(0.55 0.15 20));"
        >
          AKA
        </div>
        <div class="text-[13px]">
          <div class="font-medium text-white">Allan Kirkeby Andreasen</div>
          <div class="text-white/45">{{ "testimonialAuthorTitle" | transloco }}</div>
        </div>
      </footer>
    </div>

    <!-- Bottom utility — hairline at 10% white, with `shrink-0` so the
         flex-col parent doesn't compress its 1px height to zero. -->
    <div
      class="relative mt-8 mb-5 w-full shrink-0"
      style="min-height: 1px; height: 1px; background-color: rgba(255, 255, 255, 0.10);"
    ></div>
    <div class="relative flex items-center justify-between font-mono text-[11px] text-white/40">
      <span>v4.2.7 · 09 maj 2026</span>
      <!-- Hidden for now — demo CTA not wired up
      <a class="inline-flex items-center gap-1.5 text-white/55 hover:text-white" href="#">
        {{ "seeDemo" | transloco }}
        <igntr-icon name="arrowR" [size]="12"></igntr-icon>
      </a>
      -->
    </div>
  </aside>
</div>
